import { useAuth0 } from '@auth0/auth0-react';
import { CountryCode } from 'libphonenumber-js/min';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DreamWordmark } from '../icons/brand/dream-wordmark.svg';
import { ReactComponent as GoogleLogo } from '../icons/google-logo.svg';
import microsoftLogo from '../icons/microsoft.png';
import {
  classifyLoginIdentifier,
  DEFAULT_LOGIN_COUNTRY,
  examplePhoneNumber,
} from '../utils/loginIdentifier';
import DreamMarque from './common/DreamMarque';
import CountryCodeSelect from './CountryCodeSelect';
import './login-page.scss';

/**
 * Shown instead of blind-redirecting to Auth0 when there is no session.
 *
 * One field with a country-code picker (UK by default): a phone number starts
 * the `sms` passwordless flow; an address (anything with an @) quietly starts
 * the `email` one, which is how internal plus-addressed accounts keep signing
 * in. Either way the value goes along as `login_hint`, so Auth0 skips its
 * identifier screen and goes straight to the code. Google / Microsoft
 * deep-link to their connections; which accounts those may enter is decided
 * by the post-login Action, not here.
 */
const LoginPage: React.FC = () => {
  const { loginWithRedirect } = useAuth0();
  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState('');
  const [country, setCountry] = useState<CountryCode>(DEFAULT_LOGIN_COUNTRY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const appState = {
    path: window.location.pathname + window.location.search,
  };

  const startLogin = (connection: string, loginHint?: string) => {
    setBusy(true);
    void loginWithRedirect({
      appState,
      authorizationParams: {
        connection,
        ...(loginHint ? { login_hint: loginHint } : {}),
      },
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = classifyLoginIdentifier(identifier, country);
    if (parsed.kind === 'invalid') {
      setError(t(`login.errors.${parsed.reason}`, parsed.details));
      return;
    }
    setError(null);
    startLogin(parsed.kind, parsed.loginHint);
  };

  return (
    <div className='login-page'>
      <div className='login-content'>
        <div className='login-logo' aria-label='Dream'>
          <DreamMarque className='login-logo-icon' />
          <DreamWordmark className='login-logo-wordmark' />
        </div>
        <div className='login-text'>
          <h1 className='login-title'>{t('login.title')}</h1>
          <p className='login-lede'>{t('login.lede')}</p>
        </div>
        <form className='login-form' onSubmit={handleSubmit} noValidate>
          <label className='login-label' htmlFor='login-identifier'>
            {t('login.phoneLabel')}
          </label>
          <div className='login-phone-row'>
            <CountryCodeSelect
              value={country}
              onChange={(next) => {
                setCountry(next);
                if (error) setError(null);
              }}
              disabled={busy}
            />
            <input
              id='login-identifier'
              className={`login-input${error ? ' login-input-error' : ''}`}
              type='text'
              inputMode='tel'
              autoComplete='tel'
              autoFocus
              placeholder={examplePhoneNumber(country)}
              value={identifier}
              disabled={busy}
              onChange={(e) => {
                setIdentifier(e.target.value);
                if (error) setError(null);
              }}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'login-identifier-error' : undefined}
            />
          </div>
          {error && (
            <p id='login-identifier-error' className='login-error' role='alert'>
              {error}
            </p>
          )}
          <button
            type='submit'
            className='login-primary-button'
            disabled={busy}
          >
            {t('login.sendCode')}
          </button>
        </form>
        <div className='login-divider'>
          <span>{t('login.or')}</span>
        </div>
        <div className='login-social'>
          <button
            type='button'
            className='login-social-button'
            disabled={busy}
            onClick={() => startLogin('google-oauth2')}
          >
            <GoogleLogo />
            <span>{t('login.continueWithGoogle')}</span>
          </button>
          <button
            type='button'
            className='login-social-button'
            disabled={busy}
            onClick={() => startLogin('windowslive')}
          >
            <img src={microsoftLogo} alt='' />
            <span>{t('login.continueWithMicrosoft')}</span>
          </button>
        </div>
        <p className='login-hint'>{t('login.newHere')}</p>
      </div>
    </div>
  );
};

export default LoginPage;
